import { PRODUCT_QUERY_BASE_KEY } from '@/api/products/queries/get-products-query'
import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { OrganizationProductData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/products/[product_slug]/index'
import { keepPreviousData } from '@tanstack/react-query'

export type IProduct = OrganizationProductData
export type IProductCategory = NonNullable<IProduct['category']>

export const PRODUCT_CATEGORIES = {
    APP: 'APP',
    E_COMMERCE: 'E_COMMERCE',
    SAAS: 'SAAS',
    SERVICE: 'SERVICE',
    OTHER: 'OTHER',
} as const satisfies Partial<Record<IProductCategory, IProductCategory>>

export interface IProductQueryParams {
    organizationId: string | null
    brandSlug: string
    productUuid?: string
}

export class GetProductQuery {
    static getKey(params: IProductQueryParams) {
        return [
            ...PRODUCT_QUERY_BASE_KEY,
            params.organizationId,
            params.brandSlug,
            'get-product-query',
            params.productUuid,
        ]
    }

    static async call(queryClient: IApi, params: IProductQueryParams) {
        return queryClient
            .get<IProduct>(
                `/organization/${params.organizationId}/brands/${params.brandSlug}/products/${params.productUuid}`
            )
            .then((res) => res?.data)
    }
}

export function useGetProductQuery(productUuid?: string) {
    const { api, apiParams } = useApiToolbox()

    const organizationId = apiParams.organizationId ?? ''

    return useApiQuery({
        queryKey: GetProductQuery.getKey({
            organizationId: organizationId ?? null,
            productUuid: productUuid,
            brandSlug: apiParams.brandSlug,
        }),
        queryFn: () => GetProductQuery.call(api, { organizationId, brandSlug: apiParams.brandSlug, productUuid }),
        enabled: !!organizationId && !!productUuid && !!apiParams.brandSlug,
        placeholderData: keepPreviousData,
    })
}
